import { MeshLinkError } from '@/lib/result';
import { CONTENT_MAX_BLOB_BYTES } from '@/config';

export interface CompressedImage {
  bytes: Uint8Array;
  mime: 'image/webp' | 'image/jpeg';
  w: number;
  h: number;
}

export interface CompressOptions {
  maxEdge: number;
  quality: number;
  mime?: 'image/webp' | 'image/jpeg';
}

/** 用 Canvas 把图片压缩到指定最长边与质量（WebP 默认）。仅在浏览器可用。 */
export async function compressImage(file: Blob, opts: CompressOptions): Promise<CompressedImage> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
    throw new MeshLinkError('E_INVALID_INPUT', '当前环境不支持图片压缩');
  }
  const bmp = await createImageBitmap(file);
  try {
    const ow = bmp.width;
    const oh = bmp.height;
    const scale = Math.min(1, opts.maxEdge / Math.max(ow, oh));
    const w = Math.max(1, Math.round(ow * scale));
    const h = Math.max(1, Math.round(oh * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new MeshLinkError('E_INVALID_INPUT', '无法创建画布上下文');
    ctx.drawImage(bmp, 0, 0, w, h);
    const mime = opts.mime ?? 'image/webp';
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, mime, opts.quality));
    if (!blob) throw new MeshLinkError('E_INVALID_INPUT', '图片编码失败');
    const bytes = new Uint8Array(await blob.arrayBuffer());
    return { bytes, mime, w, h };
  } finally {
    bmp.close?.();
  }
}

/** 双档压缩：缩略图（320px）与正文图（1280px），均 WebP。
 *  正文图多级降质收敛（1280/0.82 → 1280/0.6 → 1024/0.7 → 800/0.7 → 640/0.7），
 *  直到 ≤ maxBytes（管理后台配置的图像上限）；极端内容仍超限才抛 E_CONTENT_TOO_LARGE。 */
export async function compressImageTiers(file: Blob, maxBytes: number = CONTENT_MAX_BLOB_BYTES): Promise<{ thumb: CompressedImage; full: CompressedImage }> {
  const thumb = await compressImage(file, { maxEdge: 320, quality: 0.7, mime: 'image/webp' });
  let full = await compressImage(file, { maxEdge: 1280, quality: 0.82, mime: 'image/webp' });
  const TIERS: CompressOptions[] = [
    { maxEdge: 1280, quality: 0.6, mime: 'image/webp' },
    { maxEdge: 1024, quality: 0.7, mime: 'image/webp' },
    { maxEdge: 800, quality: 0.7, mime: 'image/webp' },
    { maxEdge: 640, quality: 0.7, mime: 'image/webp' },
  ];
  for (const tier of TIERS) {
    if (thumb.bytes.length <= maxBytes && full.bytes.length <= maxBytes) break;
    full = await compressImage(file, tier);
  }
  if (thumb.bytes.length > maxBytes || full.bytes.length > maxBytes) {
    throw new MeshLinkError('E_CONTENT_TOO_LARGE');
  }
  return { thumb, full };
}
